import { ErrorCodeKey } from "lookup/ErrorCodes";

export const CONTACT_BLOCK_REASON = {
  PREVIOUSLY_MARKED_WRONG: "PREVIOUSLY_MARKED_WRONG",
  BLACKLISTED: "BLACKLISTED",
};

/** Primary phone/fax only — alternate contacts stay on the existing inline-error path. */
export const UNMARKABLE_CONTACT_FIELDS = [
  "prescriberPhone",
  "prescriberFax",
  "pharmacyPhone",
  "pharmacyFax",
];

export const CONTACT_BLOCK_COPY = {
  previouslyWrongSuffix: " was previously marked as wrong for this provider.",
  previouslyWrongPharmacySuffix: " was previously marked as wrong for this pharmacy.",
  unmarkPhone: "Unmark this phone number as wrong and keep it on the profile",
  unmarkFax: "Unmark this fax number as wrong and keep it on the profile",
  saveBlocked:
    "Update is disabled until you unmark the number(s) above, or change them.",
  saveBlockedCreate:
    "Create is disabled until blocked number(s) are changed.",
  updateAndUnmark: "Update and unmark",
  blacklisted: "This number is blacklisted and cannot be used.",
};

export const getUnmarkLabel = (fieldName) =>
  (fieldName || "").toLowerCase().includes("fax")
    ? CONTACT_BLOCK_COPY.unmarkFax
    : CONTACT_BLOCK_COPY.unmarkPhone;

export const isUnmarkableContactField = (fieldName) =>
  UNMARKABLE_CONTACT_FIELDS.includes(fieldName);

export const getContactBlockReason = (codes, fieldName) =>
  codes?.[fieldName] || CONTACT_BLOCK_REASON.PREVIOUSLY_MARKED_WRONG;

export const buildContactBlockSnapshot = (fieldErrors, fieldErrorCodes, values) => {
  const fieldNames = Object.keys(fieldErrors || {});
  return {
    fields: fieldNames,
    values: Object.fromEntries(fieldNames.map((k) => [k, values[k] ?? ""])),
    codes: fieldErrorCodes && typeof fieldErrorCodes === "object" ? fieldErrorCodes : {},
    messages: { ...(fieldErrors || {}) },
  };
};

export const isContactValueStillRejected = (snapshot, fieldName, currentValue) => {
  if (!snapshot?.fields?.includes(fieldName)) {
    return false;
  }
  return String(currentValue ?? "") === String(snapshot.values[fieldName] ?? "");
};

export const isContactSaveBlocked = ({
  snapshot,
  values,
  unmarkAck,
}) => {
  if (!snapshot?.fields?.length) {
    return false;
  }
  const stillRejected = snapshot.fields.filter((fieldName) =>
    isContactValueStillRejected(snapshot, fieldName, values[fieldName])
  );
  if (!stillRejected.length) {
    return false;
  }
  return stillRejected.some((fieldName) => {
    if (
      isUnmarkableContactField(fieldName) &&
      getContactBlockReason(snapshot.codes, fieldName) ===
        CONTACT_BLOCK_REASON.PREVIOUSLY_MARKED_WRONG
    ) {
      return !unmarkAck?.[fieldName];
    }
    return true;
  });
};

export const getUnmarkWrongContacts = (snapshot, values, unmarkAck) => {
  if (!snapshot?.fields?.length) {
    return [];
  }
  return snapshot.fields.filter((fieldName) => {
    if (!isUnmarkableContactField(fieldName)) {
      return false;
    }
    if (
      getContactBlockReason(snapshot.codes, fieldName) ===
      CONTACT_BLOCK_REASON.BLACKLISTED
    ) {
      return false;
    }
    if (!unmarkAck?.[fieldName]) {
      return false;
    }
    return isContactValueStillRejected(snapshot, fieldName, values[fieldName]);
  });
};

export const getUnmarkFlags = (snapshot, values, unmarkAck) => {
  const fields = getUnmarkWrongContacts(snapshot, values, unmarkAck);
  return {
    unmarkPhone:
      fields.includes("prescriberPhone") || fields.includes("pharmacyPhone"),
    unmarkFax:
      fields.includes("prescriberFax") || fields.includes("pharmacyFax"),
  };
};

export const isContactBlockErrorResponse = (response) =>
  response?.status === 422 &&
  response?.data?.errorCode === ErrorCodeKey.PHONE_PREVIOUSLY_MARKED_WRONG &&
  !!response?.data?.fieldErrors;

export const markContactBlockFieldsTouched = (fieldErrors, setTouched) => {
  if (typeof setTouched !== "function") {
    return;
  }
  const touchedFields = Object.keys(fieldErrors || {}).reduce(
    (acc, field) => ({ ...acc, [field]: true }),
    {}
  );
  setTouched(touchedFields, false);
};

export const applyContactBlockFormikState = ({
  response,
  values,
  setErrors,
  setTouched,
  setSubmitting,
}) => {
  if (
    !isContactBlockErrorResponse(response) ||
    typeof setErrors !== "function"
  ) {
    return null;
  }
  const snapshot = buildContactBlockSnapshot(
    response.data.fieldErrors,
    response.data.fieldErrorCodes,
    values
  );
  setErrors(response.data.fieldErrors);
  markContactBlockFieldsTouched(response.data.fieldErrors, setTouched);
  if (typeof setSubmitting === "function") {
    setSubmitting(false);
  }
  return snapshot;
};

export const getActiveContactBlockReason = ({
  snapshot,
  fieldName,
  currentValue,
  enabled = true,
  forceBlacklistedReason = false,
}) => {
  if (
    !enabled ||
    !isUnmarkableContactField(fieldName) ||
    !isContactValueStillRejected(snapshot, fieldName, currentValue)
  ) {
    return null;
  }
  if (forceBlacklistedReason) {
    return CONTACT_BLOCK_REASON.BLACKLISTED;
  }
  return getContactBlockReason(snapshot?.codes, fieldName);
};

export const getContactFieldClassName = (reason) => {
  if (reason === CONTACT_BLOCK_REASON.BLACKLISTED) {
    return "field-contact-blacklisted";
  }
  if (reason === CONTACT_BLOCK_REASON.PREVIOUSLY_MARKED_WRONG) {
    return "field-previously-wrong";
  }
  return undefined;
};

export const resolveContactFieldValue = (eventOrValue) => {
  if (
    eventOrValue &&
    typeof eventOrValue === "object" &&
    eventOrValue.target
  ) {
    return eventOrValue.target.value;
  }
  return eventOrValue;
};

export const applyNumericContactFieldChange = (value, field, setFieldValue) => {
  const nextValue = String(value ?? "").replace(/\s/g, "");
  if (nextValue === "" || /^[0-9\b]+$/.test(nextValue)) {
    setFieldValue(field, nextValue);
  }
};
